
import imageCompression from 'browser-image-compression';

export const MAX_FILE_SIZE = 10; // 10MB
export const MAX_VIDEO_DURATION = 15; // 15 seconds

export const validateFileSize = (file: File) => {
  const fileSizeInMB = file.size / (1024 * 1024);
  return fileSizeInMB <= MAX_FILE_SIZE;
};

export const validateVideo = (file: File): Promise<void> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      if (video.duration > MAX_VIDEO_DURATION) {
        reject(new Error(`Video must be ${MAX_VIDEO_DURATION} seconds or less`));
      }
      resolve();
    };

    video.onerror = () => {
      reject(new Error('Invalid video file'));
    };

    video.src = URL.createObjectURL(file);
  });
};

export const compressImage = async (file: File): Promise<File> => {
  const options = {
    maxSizeMB: MAX_FILE_SIZE,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  };

  try {
    return await imageCompression(file, options);
  } catch (error) {
    console.error('Error compressing image:', error);
    throw error;
  }
};

export const processVideoForShare = async (videoUrl: string, username: string): Promise<Blob> => {
  return new Promise(async (resolve, reject) => {
    try {
      console.log("Setting up video processing...");
      
      // Create a canvas element for video processing
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const originalVideo = document.createElement('video');
      
      // Load the original video
      console.log("Loading original video...");
      originalVideo.crossOrigin = "anonymous";
      originalVideo.src = videoUrl;
      
      await new Promise((resolve, reject) => {
        originalVideo.addEventListener('loadedmetadata', resolve);
        originalVideo.addEventListener('error', (e) => {
          reject(new Error(`Video loading error: ${e.message}`));
        });
        originalVideo.load();
      });
      
      console.log("Video loaded, setting up canvas...");
      canvas.width = originalVideo.videoWidth;
      canvas.height = originalVideo.videoHeight;

      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      // Create sound effect for end screen
      const audioCtx = new AudioContext();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      // Configure sound effect
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(440, audioCtx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(880, audioCtx.currentTime + 0.1);
      gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      // Create MediaRecorder for recording the processed video with audio
      const source = audioCtx.createMediaElementSource(originalVideo);
      const destination = audioCtx.createMediaStreamDestination();
      source.connect(destination);
      source.connect(audioCtx.destination);

      const stream = canvas.captureStream(30);
      // Combine video and audio streams
      const combinedStream = new MediaStream([
        ...stream.getVideoTracks(),
        ...destination.stream.getAudioTracks()
      ]);

      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType: 'video/webm;codecs=vp9'
      });

      const chunks: Blob[] = [];
      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = () => {
        console.log("Recording stopped, creating final video...");
        const finalVideo = new Blob(chunks, { type: 'video/webm' });
        resolve(finalVideo);
      };

      // Start recording
      console.log("Starting recording...");
      mediaRecorder.start();

      // Draw the original video
      const drawFrame = () => {
        if (originalVideo.ended) {
          // Show end screen immediately when video ends
          showEndScreen();
          return;
        }
        
        ctx!.drawImage(originalVideo, 0, 0, canvas.width, canvas.height);
        
        // Calculate position based on video progress
        const progress = originalVideo.currentTime / originalVideo.duration;
        const isSecondHalf = progress > 0.5;
        
        // Add username overlay with dynamic positioning
        ctx!.fillStyle = 'rgba(0, 0, 0, 0.5)';
        
        if (isSecondHalf) {
          // Right side position for second half
          ctx!.fillRect(canvas.width - 320, 100, 300, 50);
          ctx!.fillStyle = 'white';
          ctx!.font = '24px "SF Pro Display"';
          ctx!.textAlign = 'right';
          ctx!.fillText(`@${username} on FluxVoid`, canvas.width - 30, 130);
        } else {
          // Center position for first half
          const textWidth = 300; // Approximate width
          const xPos = (canvas.width - textWidth) / 2;
          ctx!.fillRect(xPos, 100, textWidth, 50);
          ctx!.fillStyle = 'white';
          ctx!.font = '24px "SF Pro Display"';
          ctx!.textAlign = 'center';
          ctx!.fillText(`@${username} on FluxVoid`, canvas.width / 2, 130);
        }
        
        requestAnimationFrame(drawFrame);
      };

      const showEndScreen = () => {
        // End screen with FluxVoid branding
        ctx!.fillStyle = 'black';
        ctx!.fillRect(0, 0, canvas.width, canvas.height);
        
        // Center everything
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        
        // Draw "Flux" text
        ctx!.fillStyle = 'white';
        ctx!.textAlign = 'right';
        ctx!.font = 'bold 72px "SF Pro Display"';
        const fluxWidth = ctx!.measureText('Flux').width;
        ctx!.fillText('Flux', centerX - 50, centerY);
        
        // Draw the cyan circle (blackhole)
        ctx!.beginPath();
        ctx!.arc(centerX, centerY - 10, 40, 0, Math.PI * 2);
        ctx!.fillStyle = '#00ffd5';
        ctx!.fill();
        
        // Draw "Void" text
        ctx!.fillStyle = 'white';
        ctx!.textAlign = 'left';
        ctx!.fillText('Void', centerX + 50, centerY);
        
        // Draw subtext
        ctx!.font = 'italic 24px "SF Pro Display"';
        ctx!.textAlign = 'center';
        ctx!.fillText('join us at fluxvoid.net', centerX, centerY + 60);
        
        // Play the sound effect
        oscillator.start();
        setTimeout(() => {
          oscillator.stop();
        }, 300);
      };

      // Process the video
      console.log("Starting video playback...");
      originalVideo.onplay = () => {
        drawFrame();
      };

      originalVideo.onended = () => {
        console.log("Video ended, showing end screen...");
        showEndScreen();
        
        // Show end screen for 5 seconds then stop recording
        setTimeout(() => {
          console.log("Ending recording...");
          mediaRecorder.stop();
        }, 5000);
      };

      originalVideo.play().catch((error) => {
        console.error("Video playback error:", error);
        reject(error);
      });

    } catch (error) {
      console.error("Video processing error:", error);
      reject(error);
    }
  });
};

export const isProRestrictedFile = (file: File) => {
  const proFileTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/zip',
    'application/x-zip-compressed'
  ];
  
  return proFileTypes.includes(file.type);
};

